import {
  ENTITY_TYPES,
  FieldList,
  GRID_LIST_VIEW_TYPE,
  IBaseFormService,
  IFormGroupRules,
  IFormRules,
  WIDGET_COMPONENT,
} from '../../types';
import {
  INVENTORY_COUNT_STATUS_OPTIONS,
  INVENTORY_COUNT_VIEW_OPTIONS,
} from '@shared/types/enums/inventory-count.enum';
import { InventoryCountGroupEditContainerComponent } from '@app/containers/inventory-counts/containers';
import { ValidationService } from '@components/form';
import { I18n } from '@app/i18n/strings-keys';

const GRAND_TOTALS_GRID_HEIGHT: string = '97px';

export const createFormRules = (
  formService: IBaseFormService<ENTITY_TYPES.INVENTORY_COUNT_GROUP>,
  editor: InventoryCountGroupEditContainerComponent,
): IFormRules => {
  const { fields: siteInventoryCountFields } =
    formService.getSiteInventoryCountFormRules(
      formService.formFieldPath,
      editor,
    );

  const sections: FieldList = {
    general_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.General,
    },
    grand_totals_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: I18n.Common.GrandTotals,
    },
    site_counts_section: {
      widget: WIDGET_COMPONENT.panel_group,
      title: '',
    },
  };

  const panelGroups: IFormGroupRules[] = [
    {
      name: I18n.Common.General,
      fields: [
        'general_section',
        'grand_totals_section',
        'site_counts_section',
      ],
    },
    {
      id: 'general_section',
      fields: [
        'name',
        'start_date',
        'end_date',
        'status',
        'virtual.view_setting',
      ],
    },
    {
      id: 'grand_totals_section',
      fields: ['virtual.grand_totals'],
    },
    {
      id: 'site_counts_section',
      fields: ['virtual.site_inventory_counts'],
    },
  ];

  return {
    bannerText: I18n.InfoMessages.PerformingACountWhileAnEventIsOpenMessage,
    defaultGroup: false,
    virtual: {
      type: 'object',
      properties: {
        site_inventory_counts: {
          type: 'array',
        },
        grand_totals: {
          type: 'array',
        },
        view_setting: {
          type: 'string',
          enum: ['category_view', 'alphabetical_list_view'],
        },
      },
    },
    fields: {
      ...sections,
      name: {
        title: I18n.Common.Name,
        type: 'string',
        required: true,
        disabledObs: editor.disabledNameField$,
        maxLength: 50,
        pattern: {
          value: ValidationService.PATTERNS.STRING_WITHOUT_SPECIAL_CHARACTERS,
          errorMessage: editor.i18nService.t(
            I18n.ErrorMessages.SpecialCharactersAreNotAllowed,
          ),
        },
        widgetData: {
          placeholder: editor.i18nService.t(
            I18n.Forms.InventoryCountForm.EnterInventoryCountName,
          ),
        },
        validators: [formService.inventoryCountNameValidator()],
        asyncValidators: [
          formService.inventoryCountGroupService.caseInsensitiveUniqueValidator(),
        ],
      },
      start_date: {
        title: I18n.Forms.InventoryCountForm.StartDateStartTime,
        required: true,
        disabled: true,
        widget: WIDGET_COMPONENT.input,
      },
      status: {
        title: I18n.Common.Status,
        disabled: true,
        widget: WIDGET_COMPONENT.select,
        required: false,
        selectOptions: INVENTORY_COUNT_STATUS_OPTIONS,
      },
      end_date: {
        show: false,
        defaultValue: '',
      },
      site_ids: {
        show: false,
      },
      virtual: {
        grand_totals: {
          title: '',
          widget: WIDGET_COMPONENT.edit_grid,
          class: 'grand-totals-list',
          widgetData: {
            gridHeight: GRAND_TOTALS_GRID_HEIGHT,
            useVisibleItemCountDescription: false,
            showSearch: false,
            multiselect: false,
            orderControls: false,
            disableColumnSelector: true,
            hideActionPanel: true,
            columns: [
              {
                path: 'extended_value',
                title: I18n.Forms.InventoryCountForm.ExtendedValue,
                valueFormatter:
                  formService.numberFormatService.getNumberFormatter(true),
                defaultView: true,
              },
              {
                path: 'counted_value',
                title: I18n.Forms.InventoryCountForm.CountedValue,
                valueFormatter:
                  formService.numberFormatService.getNumberFormatter(true),
                defaultView: true,
              },
              {
                path: 'variance_cost',
                title: I18n.Forms.InventoryCountForm.Variance,
                valueFormatter:
                  formService.numberFormatService.getNumberFormatter(true),
                defaultView: true,
              },
            ],
            userGridOptions: {
              overlayNoRowsTemplate: formService.i18nService.instant(
                I18n.Common.NoRowsToShow,
              ),
            },
          },
        },
        view_setting: {
          title: I18n.Forms.InventoryCountForm.SheetViewSetting,
          widget: WIDGET_COMPONENT.select,
          selectOptions: INVENTORY_COUNT_VIEW_OPTIONS,
          default: GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
          widgetData: {
            hideEmptyOption: true,
          },
        },
      },
      ...siteInventoryCountFields,
    },
    groups: [...panelGroups],
  };
};
